import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axios from '../api/axiosConfig';

// Interface reflects data from /api/settings endpoint (getSettings in controller)
// Includes all fields from the updated companySettings.json
interface Settings {
  name: string; // ТОО "Yassi Media"
  director?: string | null; // Искабеков С.Е.
  basisOfAuthority?: string | null; // Устава
  address?: string | null; // РК, 161200, Туркестанская область...
  phone?: string | null; // +7 771 033 5159
  email?: string | null; // yassi@media-agency.kz
  taxId?: string | null; // БИН 190940024938
  bankAccount?: string | null; // ИИК KZ206010001000008371
  bankName?: string | null; // АО «Народный Банк Казахстана»
  bankBik?: string | null; // БИК HSBKKZKX
  logo?: string | null; // Path to logo file or empty
  language?: string | null; // ru
  dateFormat?: string | null; // DD.MM.YYYY
  currency?: string | null; // KZT
  timezone?: string | null; // Asia/Qyzylorda
  // Add other general settings fields if they exist
  companyName?: string | null; // Original field, might be redundant if 'name' is used
  defaultCurrency?: string; // Keep if used elsewhere
  notificationsEnabled?: boolean; // Keep if used elsewhere
  gitInfo?: GitInfoProps | null; // Keep if used elsewhere
}

interface GitInfoProps {
  commitHash: string | null;
  commitDate: string | null;
  commitAuthor: string | null;
  commitCount: string | null;
  error: string | null;
}
interface SettingsContextProps {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  refetchSettings: () => void; // Function to manually refetch
}

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<Settings>('/settings/company');
      setSettings(response.data);
    } catch (err: any) {
      console.error('Error fetching settings for context:', err);
      setError(err.response?.data?.message || 'Не удалось загрузить настройки приложения.');
      setSettings(null); // Reset settings on error
      setTimeout(fetchSettings, 1000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const value = {
    settings,
    loading,
    error,
    refetchSettings: fetchSettings // Provide refetch function
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextProps => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
